import { PieceType, PlayerSide } from '../types/game.types';
import type { BoardState, GridPosition, PieceData } from '../types/game.types';

export const BOARD_SIZE = 7;

export class Board {
  private state: BoardState;
  private pieceCounter: number = 0;

  constructor() {
    this.state = this.createEmptyBoard();
  }

  private createEmptyBoard(): BoardState {
    return Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(null));
  }

  initialise(): void {
    this.state = this.createEmptyBoard();
    this.pieceCounter = 0;

    // Kiwis occupy top 2 rows (rows 0 and 1)
    for (let row = 0; row < 2; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const piece: PieceData = {
          id: `piece-${this.pieceCounter++}`,
          type: PieceType.KIWI,
          side: PlayerSide.KIWI,
          position: { row, col },
        };
        this.state[row][col] = piece;
      }
    }

    // Coconuts occupy bottom 2 rows (rows 5 and 6)
    for (let row = BOARD_SIZE - 2; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const piece: PieceData = {
          id: `piece-${this.pieceCounter++}`,
          type: PieceType.COCONUT,
          side: PlayerSide.COCONUT,
          position: { row, col },
        };
        this.state[row][col] = piece;
      }
    }
  }

  isInBounds(pos: GridPosition): boolean {
    return pos.row >= 0 && pos.row < BOARD_SIZE && pos.col >= 0 && pos.col < BOARD_SIZE;
  }

  isEmpty(pos: GridPosition): boolean {
    if (!this.isInBounds(pos)) return false;
    return this.state[pos.row][pos.col] === null;
  }

  getPiece(pos: GridPosition): PieceData | null {
    if (!this.isInBounds(pos)) return null;
    return this.state[pos.row][pos.col];
  }

  setPiece(pos: GridPosition, piece: PieceData): void {
    if (!this.isInBounds(pos)) return;
    piece.position = { ...pos };
    this.state[pos.row][pos.col] = piece;
  }

  removePiece(pos: GridPosition): PieceData | null {
    if (!this.isInBounds(pos)) return null;
    const piece = this.state[pos.row][pos.col];
    this.state[pos.row][pos.col] = null;
    return piece;
  }

  getAllPieces(): PieceData[] {
    const pieces: PieceData[] = [];
    for (let row = 0; row < BOARD_SIZE; row++) {
      for (let col = 0; col < BOARD_SIZE; col++) {
        const piece = this.state[row][col];
        if (piece !== null) {
          pieces.push(piece);
        }
      }
    }
    return pieces;
  }

  getPiecesBySide(side: PlayerSide): PieceData[] {
    return this.getAllPieces().filter((p) => p.side === side);
  }

  getState(): BoardState {
    return this.state;
  }

  clone(): Board {
    const copy = new Board();
    copy.state = this.state.map((row) =>
      row.map((cell) => (cell ? { ...cell, position: { ...cell.position } } : null)),
    );
    copy.pieceCounter = this.pieceCounter;
    return copy;
  }
}
